// In-memory test doubles for the experience ports. They record what was asked of
// them and let a test push frames at the session on demand. Exported so the two
// glasses clients can reuse them in their own tests.
import type {
  AuthCredentials,
  AuthPort,
  AuthSession,
} from '../../ports/AuthPort';
import type {
  DiscoveredExperience,
  ExperienceDiscoveryPort,
  ExperienceDiscoveryResult,
} from '../../ports/ExperienceDiscoveryPort';
import type {
  ActionResponse,
  BootstrapBindings,
  ExperienceActionIntent,
  ExperienceConfig,
  ExperienceConnection,
  ExperienceEventsBatch,
  ExperiencePort,
  MissionUpdate,
  TalkActionIntent,
  TextTurnIntent,
  Utterance,
} from '../../ports/ExperiencePort';
import type { ErrorFrame } from '../../ports/ErrorFrame';

export interface FakeAuthOptions {
  /** How long after the call the returned session expires. */
  expiresInMs?: number;
  accessToken?: string;
  refreshToken?: string;
}

export class FakeAuth implements AuthPort {
  loginCalls: AuthCredentials[] = [];
  refreshCalls: string[] = [];
  logoutCalls: string[] = [];
  /** Set to make the next login reject. */
  loginError: Error | null = null;
  /** Set to make the next refresh reject. */
  refreshError: Error | null = null;
  /** Incremented per issued session so tokens are distinguishable. */
  private issued = 0;

  constructor(private readonly options: FakeAuthOptions = {}) {}

  async login(credentials: AuthCredentials): Promise<AuthSession> {
    this.loginCalls.push(credentials);
    if (this.loginError) throw this.loginError;
    return this.issue();
  }

  async refresh(refreshToken: string): Promise<AuthSession> {
    this.refreshCalls.push(refreshToken);
    if (this.refreshError) throw this.refreshError;
    return this.issue();
  }

  async validate(): Promise<boolean> {
    return true;
  }

  async logout(accessToken: string): Promise<void> {
    this.logoutCalls.push(accessToken);
  }

  private issue(): AuthSession {
    this.issued += 1;
    const base = this.options.accessToken ?? 'token';
    return {
      accessToken: this.issued === 1 ? base : `${base}-${this.issued}`,
      refreshToken: this.options.refreshToken,
      expiresAt: Date.now() + (this.options.expiresInMs ?? 3_600_000),
    };
  }
}

export function fakeExperienceRecord(
  id = 'exp-1',
  displayName = 'Mission Unpossible'
): DiscoveredExperience {
  return {
    id,
    experienceId: id,
    displayName,
    clientBootstrapRequired: false,
  };
}

export class FakeDiscovery implements ExperienceDiscoveryPort {
  calls: string[] = [];
  /** Set to make the next discovery reject. */
  error: Error | null = null;
  result: ExperienceDiscoveryResult;

  constructor(result?: Partial<ExperienceDiscoveryResult>) {
    const experience = result?.experience ?? fakeExperienceRecord();
    this.result = {
      defaultExperienceId: result?.defaultExperienceId ?? experience.id,
      experience,
      experiences: result?.experiences ?? [experience],
    };
  }

  async discoverDefaultExperience(token: string): Promise<ExperienceDiscoveryResult> {
    this.calls.push(token);
    if (this.error) throw this.error;
    return this.result;
  }
}

export interface FakeConnectCall {
  token: string;
  experienceId: string;
  /**
   * 'caller' for a connect the session asked for, 'adapter' for one the fake
   * socket made on its own after a drop, the way the real adapter retries.
   */
  source: 'caller' | 'adapter';
}

export class FakeExperience implements ExperiencePort {
  connectCalls: FakeConnectCall[] = [];
  disconnectCount = 0;
  /** Tokens passed to updateToken, in order. */
  tokenUpdates: string[] = [];
  /** Payloads passed to sendAction, in order. */
  sent: ExperienceActionIntent[] = [];
  /** Intents passed to sendTalk, in order. */
  talks: TalkActionIntent[] = [];
  /** Turns passed to sendTextTurn, in order. */
  textTurns: TextTurnIntent[] = [];
  pings = 0;
  locations: Array<{ lat: number; lng: number; accuracy?: number }> = [];
  /** session_id values passed to sendRequestConfig, in order. */
  requestConfigCalls: string[] = [];
  /** Batches passed to sendEvents, in order. */
  eventBatches: ExperienceEventsBatch[] = [];
  /** Set to make the next connect throw. */
  connectError: Error | null = null;
  /** Set to make the next sendTalk throw. */
  sendError: Error | null = null;
  handlers: ExperienceConnection | null = null;
  /** How long the fake waits before its own retry, like the adapter backoff. */
  retryDelayMs = 1000;
  /** Bindings the fake sends with the connected frame of its own retry. */
  retryBindings: BootstrapBindings = fakeBindings();

  private token = '';
  private experienceId = '';
  private closedByCaller = false;

  connect(token: string, experienceId: string, handlers: ExperienceConnection): void {
    this.connectCalls.push({ token, experienceId, source: 'caller' });
    if (this.connectError) throw this.connectError;
    this.token = token;
    this.experienceId = experienceId;
    this.handlers = handlers;
    this.closedByCaller = false;
  }

  disconnect(): void {
    this.disconnectCount += 1;
    this.closedByCaller = true;
    this.handlers = null;
  }

  updateToken(token: string): void {
    this.tokenUpdates.push(token);
    this.token = token;
  }

  sendTalk(action: TalkActionIntent): void {
    if (this.sendError) throw this.sendError;
    this.talks.push(action);
  }

  sendTextTurn(turn: TextTurnIntent): void {
    if (this.sendError) throw this.sendError;
    this.textTurns.push(turn);
  }

  sendAction(action: ExperienceActionIntent): void {
    if (this.sendError) throw this.sendError;
    this.sent.push(action);
  }

  sendPing(): void {
    this.pings += 1;
  }

  sendLocation(lat: number, lng: number, accuracy?: number): void {
    this.locations.push({ lat, lng, accuracy });
  }

  sendRequestConfig(sessionId: string): void {
    this.requestConfigCalls.push(sessionId);
  }

  sendEvents(batch: ExperienceEventsBatch): void {
    this.eventBatches.push(batch);
  }

  emitConnected(bindings: BootstrapBindings): void {
    this.handlers?.onConnected(bindings);
  }

  emitUtterance(utterance: Utterance): void {
    this.handlers?.onUtterance?.(utterance);
  }

  emitConfig(config: ExperienceConfig): void {
    this.handlers?.onConfig?.(config);
  }

  emitActionResponse(response: ActionResponse): void {
    this.handlers?.onActionResponse(response);
  }

  emitMissionUpdate(update: MissionUpdate): void {
    this.handlers?.onMissionUpdate(update);
  }

  emitError(frame: ErrorFrame): void {
    this.handlers?.onError(frame);
  }

  /**
   * Models the real socket: tell the session the connection dropped, then, if
   * nobody closed it deliberately, retry on our own after the backoff delay.
   */
  emitDisconnected(reason: string): void {
    this.handlers?.onDisconnected(reason);
    if (!this.handlers || this.closedByCaller) return;
    setTimeout(() => {
      if (!this.handlers || this.closedByCaller) return;
      this.connectCalls.push({
        token: this.token,
        experienceId: this.experienceId,
        source: 'adapter',
      });
      this.handlers.onConnected(this.retryBindings);
    }, this.retryDelayMs);
  }
}

export function fakeBindings(overrides: Partial<BootstrapBindings> = {}): BootstrapBindings {
  return {
    sessionId: 'session-1',
    experienceId: 'exp-1',
    experienceName: 'Mission Unpossible',
    roomInstanceId: 'room-1',
    activeActorId: 'actor-1',
    activeActorName: 'Agent',
    activeActorRole: 'participant',
    characterInstanceId: 'character-1',
    participantActorId: 'participant-1',
    ...overrides,
  };
}
